import styled from "styled-components";
import { ColorVariables } from "manifest-components/src/Colors";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { getMobileBreakpoint } from "manifest-components/src/Styles/globals";
import { TransferContext } from "../../context/Transfer/transferProvider";
import { TransferStatusContext } from "../../context/TransferStatus/transferStatusProvider";
import { RoutePaths, useTypedLocation } from "../../routes/RoutePaths";
import { ReactComponent as CompletedIcon } from "./completed.svg";
import { ReactComponent as IncompleteIcon } from "./incomplete.svg";
import { ReactComponent as ActiveIcon } from "./active.svg";
import { ReactComponent as ActiveIconWithTick } from "./activeWithTick.svg";
import Authentication from "../../utils/authentication";
import { AppContext } from "../../context/App/appProvider";

type IconStates = "active" | "completed" | "incomplete" | "active completed";

const StyledStepContainer = styled.div<{ isMobile?: boolean }>`
  width: 550px;
  position: absolute;
  left: calc(50% - 275px);
  top: 16px;
  display: ${(props) => {
    if (props.isMobile) return "none";
    return "flex";
  }};
  @media (max-width: ${getMobileBreakpoint}) {
    width: 100%;
    position: relative;
    left: initial;
    right: initial;
    top: initial;
    display: ${(props) => {
      if (props.isMobile) return "flex";
      return "none";
    }};
  }
`;

const StyledStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  width: 33%;
  p {
    margin-top: 5px;
    color: ${ColorVariables.coco3};
  }
  .imageContainer {
    width: 45px;
    height: 45px;
    svg {
      padding: 7px;
      width: 100%;
      height: 100%;
    }
  }
  &:after {
    height: 3px;
    display: block;
    right: calc(50% + 25px);
    background: ${ColorVariables.coco3};
    left: calc(-25% - 5px);
    top: 20px;
    border-radius: 7px;
    position: absolute;
    content: " ";
  }
  &:first-child:after {
    display: none;
  }
  &.completed {
    &:after {
      background: ${ColorVariables.mint};
    }
  }
  &.active {
    svg {
      padding: 0px;
    }
    p {
      font-weight: 400;
    }
    &:after {
      background: ${ColorVariables.mint};
      right: calc(50% + 30px);
    }
    + * {
      &:after {
        background: ${ColorVariables.mint};
        left: calc(-25% - 10px);
      }
    }
  }
  &.active + * {
    &:after {
      background: ${ColorVariables.mint3};
    }
  }
  &.completed,
  &.active {
    p {
      color: ${ColorVariables.coco};
    }
  }
  &:last-child {
    padding-right: 0;
  }
  @media (max-width: ${getMobileBreakpoint}) {
    margin-bottom: 30px;
    .imageContainer {
      width: 38px;
      height: 38px;
      svg {
        padding: 7px;
        width: 100%;
        height: 100%;
      }
    }
    &:after {
      left: calc(-25% - 5px);
      top: 17px;
      right: calc(50% + 25px);
    }
    &.active {
      svg {
        padding: 0px;
      }
      &:after {
        background: ${ColorVariables.mint};
        right: calc(50% + 26px);
      }
      + * {
        &:after {
          background: ${ColorVariables.mint};
          left: calc(-25% + 3px);
          right: calc(50% + 20px);
        }
      }
    }
  }
`;

const getClassNameForStep = (props: {
  activeStepNumber: number;
  stepNumber: number;
  isCompleted: boolean;
}): IconStates => {
  const { activeStepNumber, stepNumber, isCompleted } = props;
  if (activeStepNumber === stepNumber) {
    if (isCompleted) {
      return "active completed";
    }
    return "active";
  }
  if (isCompleted) return "completed";
  return "incomplete";
};

const IconForStep = (props: { state: IconStates }) => {
  const { state } = props;
  if (state === "active") {
    return <ActiveIcon />;
  }
  if (state === "completed") {
    return <CompletedIcon />;
  }
  if (state === "active completed") {
    return <ActiveIconWithTick />;
  }
  return <IncompleteIcon />;
};

// eslint-disable-next-line react/no-unused-prop-types
const IconAndText = (props: { activeStepNumber: number; stepNumber: number; steps: JSX.Element[]; isCompleted }) => {
  const { stepNumber, steps } = props;
  const className = getClassNameForStep(props);
  const stepElement = steps[stepNumber - 1];
  return (
    <StyledStep key={stepNumber} className={className}>
      <div className="imageContainer">
        <IconForStep state={className} />
      </div>
      {stepElement}
    </StyledStep>
  );
};

export function useShowProgressBar() {
  const { currentTransfer } = useContext(TransferContext);
  const { currentTransferInstruction } = useContext(TransferStatusContext);
  const location = useTypedLocation();

  return useMemo(() => {
    const routesToHideProgressBar = [
      RoutePaths.home,
      RoutePaths.settings,
      RoutePaths.accountDetails,
      RoutePaths.changePassword,
      RoutePaths.transferSteps,
    ];
    if (!Authentication.verify()) return false;
    if (routesToHideProgressBar.indexOf(location.pathname as any) > -1) return false;
    if (location.state?.transferInstructionId) return false;
    if (!location.state?.transferId) return false;
    if (!currentTransfer && !currentTransferInstruction) return false;
    return true;
  }, [location.pathname, Authentication.verify(), currentTransfer, currentTransferInstruction]);
}

export default function TransferProgressBar(props: { isMobile?: boolean }) {
  const { isMobile } = props;
  const location = useTypedLocation();
  const { currentTransferFetched, currentTransfer, fetchingCurrentTransfer, getTransfer } = useContext(TransferContext);
  const shouldShowProgressBar = useShowProgressBar();

  const { employerDetails, getEmployerDetails, employerDetailsFetched } = useContext(AppContext);

  const enableEmployerSpecificFlows = employerDetails?.enableEmployerSpecificFlows || false;

  const stepsToUse = useMemo(() => {
    const steps = [
      <p key={0} className="xsmall">
        Locate <br /> old accounts
      </p>,
      enableEmployerSpecificFlows ? (
        <p key={1} className="xsmall">
          Connect <br /> destination account
        </p>
      ) : (
        <p key={1} className="xsmall">
          Explore <br /> destination options
        </p>
      ),
      <p key={2} className="xsmall">
        Review <br /> transfer details
      </p>,
    ];
    const mobileSteps = [
      <p key={0} className="xsmall">
        Old <br /> accounts
      </p>,
      enableEmployerSpecificFlows ? (
        <p key={1} className="xsmall">
          Destination <br /> account
        </p>
      ) : (
        <p key={1} className="xsmall">
          Destination <br /> options
        </p>
      ),
      <p key={2} className="xsmall">
        Transfer <br /> details
      </p>,
    ];
    if (isMobile) return mobileSteps;
    return steps;
  }, [isMobile, enableEmployerSpecificFlows]);

  // Steps start at 1
  // If we are on the transfer actions page, it's the only spot in the app where all steps should look completed
  const activeStepNumber = useMemo(() => {
    if (location.pathname === RoutePaths.transferActions) return 5;
    if (location.pathname === RoutePaths.connectOldAccount) return 1;
    if (location.pathname === RoutePaths.connectDestination) return 2;
    if (location.pathname === RoutePaths.transferDetails) return 3;
    return currentTransfer?.currentStepNumber;
  }, [currentTransfer, location.state, location.pathname]);

  const completedSteps = useMemo(() => {
    if (location.pathname === RoutePaths.transferActions) return 5;
    return currentTransfer?.currentStepNumber;
  }, [currentTransfer, location.state, location.pathname]);

  const loadData = useCallback(async () => {
    if (location.state?.transferId && !currentTransferFetched && !fetchingCurrentTransfer) {
      getTransfer(location.state.transferId);
    }
    if (!employerDetailsFetched && Authentication.verify()) {
      getEmployerDetails();
    }
  }, [
    employerDetailsFetched,
    Authentication.verify(),
    location.state?.transferId,
    currentTransferFetched,
    fetchingCurrentTransfer,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!employerDetailsFetched) return null;
  if (!shouldShowProgressBar) return null;

  return (
    <StyledStepContainer id="transferProgressBar" isMobile={isMobile}>
      {stepsToUse.map((step, stepNumber) => (
        <IconAndText
          key={stepNumber}
          activeStepNumber={activeStepNumber}
          stepNumber={stepNumber + 1}
          steps={stepsToUse}
          isCompleted={stepNumber + 1 < completedSteps}
        />
      ))}
    </StyledStepContainer>
  );
}
